import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { memo } from "react";
import CustomizedSwitch from "../../../../../common/formElements/Switch";
import { FastField, FormikProps, useFormikContext } from "formik";
import { Data } from "../../../../../store/terminal/types";
import pageStyles from "../../../../pageStyles";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";

interface Props {
  isUpdateAllowed?: boolean;
  bulk: boolean;
  shouldHideField: (fieldName: string) => boolean;
}

const EditPayAtTableComponent = ({ isUpdateAllowed, bulk, shouldHideField }: Props) => {
  const formProps = useFormikContext<FormikProps<Data>>();
  const { setFieldValue } = formProps;
  const values = (formProps.values as unknown) as Data;
  const errors = (formProps.errors as unknown) as Data;
  const classes = pageStyles();

  const payAtTableTooltip = {
    HostMode:
      "The interface through which Pay At Table interacts with the Table host",
    HostURL: "The main url used for the table payment processing",
    MerchantPassword:
      "Sets the password that is used to access the settings in the Pay At Table application",
    StoreNumber:
      "Unique identifier for a store/venue provided by the POS provider",
    UserNumber:
      "The default User Number value used for when querying table information",
    ClientId: "Unique identifier assigned by POS provider to an organization",
    ClientSecret:
      "Unique code (associated to the ClientId) that POS provider uses to verify an organization",
    PrintMerchantReceipt:
      "When enabled, merchant receipt will print automatically",
    EnableSplitPayment:
      "Enabling allows for multiple forms of payment to be accepted for a single transaction",
    TokenHostEndpoint:
      "Used to specify the URL of the endpoint on the host server where tokens will be generated",
    TokenHostAudience:
      "Used to specify the intended audience for tokens generated by the host endpoint. This is used to ensure that tokens can only be used by authorized parties and can help to prevent unauthorized access to protected resources",
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.HostMode}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="HostMode">Host mode</InputLabel>
            <FastField
              as={Select}
              name="attributes.PayAtTable.HostMode"
              labelId="HostMode"
              label="Host mode"
              variant="outlined"
              value={values.attributes.PayAtTable?.HostMode || "OPI"}
              disabled={!isUpdateAllowed}
            >
              <MenuItem value="OPI">OPI Handling</MenuItem>
              <MenuItem value="MAITRED">Maitre&apos;D Handling</MenuItem>
              <MenuItem value="HL">H&L Handling</MenuItem>
            </FastField>
          </FormControl>
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.HostURL}>
          <FastField
            label="Host URL"
            name="attributes.PayAtTable.HostURL"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.HostURL}
            helperText={errors?.attributes?.PayAtTable?.HostURL}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.MerchantPassword}>
          <FastField
            label="Merchant Password"
            name="attributes.PayAtTable.MerchantPassword"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.MerchantPassword}
            helperText={errors?.attributes?.PayAtTable?.MerchantPassword}
            fullWidth
            defaultValue="0000"
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.StoreNumber}>
          <FastField
            label="Store Number"
            name="attributes.PayAtTable.StoreNumber"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.StoreNumber}
            helperText={errors?.attributes?.PayAtTable?.StoreNumber}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.UserNumber}>
          <FastField
            label="User Number"
            name="attributes.PayAtTable.UserNumber"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.UserNumber}
            helperText={errors?.attributes?.PayAtTable?.UserNumber}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.ClientId}>
          <FastField
            label="Client ID"
            name="attributes.PayAtTable.ClientId"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.ClientId}
            helperText={errors?.attributes?.PayAtTable?.ClientId}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.ClientSecret}>
          <FastField
            label="Client Secret"
            name="attributes.PayAtTable.ClientSecret"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.ClientSecret}
            helperText={errors?.attributes?.PayAtTable?.ClientSecret}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.TokenHostEndpoint}>
          <FastField
            label="Token Host Endpoint"
            name="attributes.PayAtTable.TokenHostEndpoint"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.TokenHostEndpoint}
            helperText={errors?.attributes?.PayAtTable?.TokenHostEndpoint}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip={payAtTableTooltip.TokenHostAudience}>
          <FastField
            label="Token Host Audience"
            name="attributes.PayAtTable.TokenHostAudience"
            variant="outlined"
            as={TextField}
            error={errors?.attributes?.PayAtTable?.TokenHostAudience}
            helperText={errors?.attributes?.PayAtTable?.TokenHostAudience}
            fullWidth
            disabled={!isUpdateAllowed}
          />
        </OptionalToolTip>
      </Grid>

      <Grid item xs={12}>
        <hr className={classes.devide} />
        <Box p={2} pt={0}>
          <OptionalToolTip tooltip={payAtTableTooltip.PrintMerchantReceipt}>
            <FormControlLabel
              style={{ marginRight: "3rem" }}
              control={
                <CustomizedSwitch
                  key={`${values.attributes.PayAtTable?.PrintMerchantReceipt}`}
                  checked={
                    values.attributes.PayAtTable
                      ?.PrintMerchantReceipt as boolean
                  }
                  onChange={(event) =>
                    setFieldValue(
                      "attributes.PayAtTable.PrintMerchantReceipt",
                      event.target.checked
                    )
                  }
                  disabled={!isUpdateAllowed}
                />
              }
              label="Print Merchant Receipt"
            />
          </OptionalToolTip>

          <OptionalToolTip tooltip={payAtTableTooltip.EnableSplitPayment}>
            <FormControlLabel
              style={{ marginRight: "3rem" }}
              control={
                <CustomizedSwitch
                  key={`${values.attributes.PayAtTable?.EnableSplitPayment}`}
                  checked={
                    values.attributes.PayAtTable?.EnableSplitPayment as boolean
                  }
                  onChange={(event) =>
                    setFieldValue(
                      "attributes.PayAtTable.EnableSplitPayment",
                      event.target.checked
                    )
                  }
                  disabled={!isUpdateAllowed}
                />
              }
              label="Enable Split Payment"
            />
          </OptionalToolTip>
        </Box>
      </Grid>
    </Grid>
  );
};

export default memo(EditPayAtTableComponent);
